// ExtractionStatusButton - row-level toggle that says how the file's extraction
// went and how big it is, and opens the inline ExtractedContentPanel below.
// Reads the same view the panel does (one batched query under a provider).
import { useUploadExtracted } from "../api";
import { sizeLabel } from "./extractionCounts";
import { isTextSelectionClick, SELECTABLE_LABEL_STYLE } from "./selectableButton";

interface Props {
  uploadId: string;
  isExpanded: boolean;
  onToggle: () => void;
  // Size of the source document, from the row (NOT the panel query): the label has
  // to render before anyone opens the panel, and the text record that knows the
  // page count is fetched lazily on expand.
  chars?: number | null;
  pages?: number | null;
  // From the row: when a thread the mailbox import is still adding to will be
  // extracted (ISO), or null when it is not held.
  heldUntil: string | null | undefined;
}

const BTN_STYLE: React.CSSProperties = {
  marginLeft: 6,
  padding: "1px 6px",
  background: "transparent",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  fontSize: 11,
  color: "#374151",
  cursor: "pointer",
  // Wraps rather than overflowing the row on a narrow screen.
  maxWidth: "100%",
  textAlign: "left",
  ...SELECTABLE_LABEL_STYLE,
};

const labelFor = (
  status: "pending" | "succeeded" | "failed" | "unsupported" | undefined,
  size: string | null,
  isExpanded: boolean,
  heldUntil: string | null | undefined,
): { text: string; color: string } => {
  const prefix = `${isExpanded ? "Hide" : "Show"} extracted content`;
  if (status === "pending" && heldUntil) {
    const at = new Date(heldUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return {
      text: `${prefix} (waiting for the mailbox import to finish - extracts from ${at})`,
      color: "#6b7280",
    };
  }
  if (status === "succeeded") {
    return { text: size ? `${prefix} - ${size}` : prefix, color: "#374151" };
  }
  if (status === "failed") {
    return { text: `${prefix} (extraction failed)`, color: "#b91c1c" };
  }
  if (status === "unsupported") {
    return { text: `${prefix} (unsupported)`, color: "#6b7280" };
  }
  if (status === "pending") {
    return { text: `${prefix} (extracting…)`, color: "#6b7280" };
  }
  return { text: prefix, color: "#374151" };
};

export const ExtractionStatusButton = ({
  uploadId,
  isExpanded,
  onToggle,
  chars,
  pages,
  heldUntil,
}: Props): JSX.Element => {
  const { data } = useUploadExtracted(uploadId);
  const size = sizeLabel(chars, pages);
  const { text, color } = labelFor(data?.extraction_status, size, isExpanded, heldUntil);

  return (
    <button
      type="button"
      onClick={(e) => {
        if (isTextSelectionClick(e)) return;
        onToggle();
      }}
      aria-expanded={isExpanded}
      style={{ ...BTN_STYLE, color }}
      title={
        data?.extraction_status === "succeeded"
          ? "Open to read the summary and text the agent sees, and what the extraction cost."
          : (data?.extraction_error ?? "Show extracted content")
      }
    >
      {text}
    </button>
  );
};
